import React, { useState } from "react";
import {
  Modal,
  Table,
  Button,
  Radio,
  Space,
  Typography,
  Tag,
  Divider,
  Alert,
} from "antd";
import { SwapOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import {
  GET_TRANSFER_STUDENTS,
  GET_TRANSFER_AVAILABLE_TARGETS,
  POST_TRANSFER_USERS,
  POST_TRANSFER_ALL_USERS,
} from "../../Utlis/apiPath";
import { getApi, postApi } from "../../Utlis/apiService";
import { errorToast, successToast } from "../../Services/ToastHelper";

const { Title, Text } = Typography;

const TransferModal = ({ visible, onClose, sourceClass }) => {
  const [step, setStep] = useState(1); // 1 = students, 2 = target classes
  const [isLoading, setIsLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [selectedBookingIds, setSelectedBookingIds] = useState([]);
  const [availableTargets, setAvailableTargets] = useState({
    standard: [],
    daylight: [],
  });
  const [selectedTargetClassId, setSelectedTargetClassId] = useState(null);
  const [totalStudents, setTotalStudents] = useState(0);
  const [studentsLoaded, setStudentsLoaded] = useState(false);

  const fetchStudents = async () => {
    if (!sourceClass?.id) return;
    setIsLoading(true);
    try {
      const { status, data } = await getApi(GET_TRANSFER_STUDENTS, {
        params: { classId: sourceClass.id },
      });
      if (status === 200) {
        setStudents(data.bookings || []);
        setTotalStudents(data.totalStudents || 0);
        setStudentsLoaded(true);
      } else {
        setStudents([]);
      }
    } catch (error) {
      console.error("Error fetching students:", error);
      setStudents([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAvailableTargets = async () => {
    if (!sourceClass?.id) return;
    setIsLoading(true);
    try {
      const { status, data } = await getApi(GET_TRANSFER_AVAILABLE_TARGETS, {
        params: { sourceClassId: sourceClass.id },
      });
      if (status === 200) {
        setAvailableTargets({
          standard: data.standard || [],
          daylight: data.daylight || [],
        });
        setStep(2);
      } else {
        errorToast("Failed to load available target classes.");
      }
    } catch (error) {
      console.error("Error fetching available targets:", error);
      errorToast("Failed to load available target classes.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleTransferConfirm = async () => {
    if (!selectedTargetClassId) {
      errorToast("Please select a target class.");
      return;
    }

    const isAllSelected = selectedBookingIds.length === students.length;
    setIsLoading(true);
    try {
      let status, message;

      if (isAllSelected) {
        ({ status, message } = await postApi(POST_TRANSFER_ALL_USERS, {
          sourceClassId: sourceClass.id,
          targetClassId: selectedTargetClassId,
        }));
      } else {
        ({ status, message } = await postApi(POST_TRANSFER_USERS, {
          sourceClassId: sourceClass.id,
          targetClassId: selectedTargetClassId,
          bookingIds: selectedBookingIds,
        }));
      }

      if (status === 200) {
        successToast(message || "Students transferred successfully.");
        handleClose();
      } else {
        errorToast(message || "Transfer failed.");
      }
    } catch (error) {
      console.error("Error transferring students:", error);
      errorToast("An error occurred during transfer.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setStudents([]);
    setSelectedBookingIds([]);
    setAvailableTargets({ standard: [], daylight: [] });
    setSelectedTargetClassId(null);
    setStudentsLoaded(false);
    onClose();
  };

  const handleOpenModal = () => {
    fetchStudents();
  };

  const rowSelection = {
    selectedRowKeys: selectedBookingIds,
    onChange: (selectedKeys) => {
      setSelectedBookingIds(selectedKeys);
    },
  };

  const studentColumns = [
    {
      title: "S.No",
      key: "slNo",
      render: (_, __, index) => index + 1,
      width: 60,
    },
    {
      title: "Name",
      key: "name",
      render: (_, record) =>
        `${record.customerId?.firstName || ""} ${record.customerId?.lastName || ""}`.trim() ||
        "N/A",
    },
    {
      title: "Email",
      key: "email",
      render: (_, record) => record.customerId?.email || "N/A",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "completed" ? "green" : "orange"}>
          {status?.charAt(0).toUpperCase() + status?.slice(1)}
        </Tag>
      ),
    },
    {
      title: "Booking Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => (date ? new Date(date).toLocaleDateString() : "N/A"),
    },
  ];

  const TargetClassOption = ({ cls }) => (
    <div
      style={{
        padding: "10px 14px",
        border: `2px solid ${selectedTargetClassId === cls.id ? "#1890ff" : "#e8e8e8"}`,
        borderRadius: 8,
        cursor: "pointer",
        marginBottom: 8,
        background: selectedTargetClassId === cls.id ? "#e6f7ff" : "#fff",
        transition: "all 0.2s",
      }}
      onClick={() => setSelectedTargetClassId(cls.id)}
    >
      <Radio checked={selectedTargetClassId === cls.id} style={{ marginRight: 8 }} />
      <Text strong>{cls.title}</Text>
    </div>
  );

  const modalTitle = (
    <Space>
      <SwapOutlined />
      <span>
        Transfer Students —{" "}
        <Text type="secondary" style={{ fontWeight: "normal", fontSize: 14 }}>
          {sourceClass?.title}
        </Text>
      </span>
    </Space>
  );

  const footerStep1 = [
    <Button key="cancel" onClick={handleClose}>
      Cancel
    </Button>,
    <Button
      key="next"
      type="primary"
      icon={<SwapOutlined />}
      disabled={selectedBookingIds.length === 0}
      onClick={fetchAvailableTargets}
      loading={isLoading}
    >
      Transfer {selectedBookingIds.length > 0 ? `(${selectedBookingIds.length})` : ""}
    </Button>,
  ];

  const footerStep2 = [
    <Button key="back" icon={<ArrowLeftOutlined />} onClick={() => setStep(1)}>
      Back
    </Button>,
    <Button key="cancel" onClick={handleClose}>
      Cancel
    </Button>,
    <Button
      key="confirm"
      type="primary"
      disabled={!selectedTargetClassId}
      onClick={handleTransferConfirm}
      loading={isLoading}
    >
      Confirm Transfer
    </Button>,
  ];

  return (
    <Modal
      title={modalTitle}
      open={visible}
      onCancel={handleClose}
      width={800}
      footer={step === 1 ? footerStep1 : footerStep2}
      afterOpenChange={(open) => {
        if (open && !studentsLoaded) handleOpenModal();
      }}
      destroyOnClose
    >
      {step === 1 && (
        <div>
          {totalStudents > 0 && (
            <Alert
              message={`${totalStudents} active student(s) found in "${sourceClass?.title}"`}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          <Table
            rowKey="id"
            columns={studentColumns}
            dataSource={students}
            rowSelection={rowSelection}
            loading={isLoading}
            pagination={false}
            scroll={{ y: 350 }}
            locale={{ emptyText: "No active students found for this class." }}
          />
          {students.length > 0 && (
            <div style={{ marginTop: 8, color: "#888", fontSize: 13 }}>
              {selectedBookingIds.length === students.length
                ? "All students selected — will use bulk transfer."
                : selectedBookingIds.length > 0
                ? `${selectedBookingIds.length} student(s) selected — will transfer individually.`
                : "Select students to transfer."}
            </div>
          )}
        </div>
      )}

      {step === 2 && (
        <div>
          <Alert
            message={`Transferring ${selectedBookingIds.length === students.length ? "all" : selectedBookingIds.length} student(s). Select a target class below.`}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          {availableTargets.standard.length > 0 && (
            <>
              <Title level={5} style={{ marginBottom: 8 }}>
                Standard Classes
              </Title>
              {availableTargets.standard.map((cls) => (
                <TargetClassOption key={cls.id} cls={cls} />
              ))}
              <Divider />
            </>
          )}

          {availableTargets.daylight.length > 0 && (
            <>
              <Title level={5} style={{ marginBottom: 8 }}>
                Daylight Classes
              </Title>
              {availableTargets.daylight.map((cls) => (
                <TargetClassOption key={cls.id} cls={cls} />
              ))}
            </>
          )}

          {availableTargets.standard.length === 0 &&
            availableTargets.daylight.length === 0 && (
              <Alert
                message="No available target classes found."
                type="warning"
                showIcon
              />
            )}
        </div>
      )}
    </Modal>
  );
};

export default TransferModal;
